import { useAuth0 } from '@auth0/auth0-react';
import { useBackoffice } from '../../backoffice/useBackoffice';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';

export type ExternalAccount = {
  id: string;
  bank_name: string;
  account_holder_type: string;
  account_holder_first_name: string | null;
  account_holder_last_name: string | null;
  account_holder_business_name: string | null;
  account_type: string;
  account_holder_address: {
    country: string;
    city: string;
    street_line_1: string;
    postal_code: string;
  };
  iban?: {
    iban_number: string;
    bic: string;
  };
  us?: {
    account_number: string;
    routing_number: string;
  };
  international_wire?: {
    account_number: string;
    routing_number: string;
    bic: string;
  };
  created_at: string;
};

export const useExternalAccounts = (customerId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const { developerId } = useBackoffice();
  const query = useQuery({
    queryKey: ['external-accounts', customerId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.get<{ data: { items: ExternalAccount[] } }>(
        `${process.env.REACT_APP_BACKEND_API_URL}/customers/${customerId}/external-accounts`,
        {
          params: {
            consumerId: developerId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data?.data;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
    enabled: !!customerId,
  });

  return query;
};


export const renderMaskedAccountTitle = (acc: ExternalAccount): string => {
  // Get account holder name based on type (individual vs business)
  const accountName =
    acc.account_holder_type === 'business'
      ? acc.account_holder_business_name ?? ''
      : `${acc.account_holder_first_name ?? ''} ${acc.account_holder_last_name ?? ''}`.trim();

  // Get masked account number based on account type
  let maskedNumber = '';
  if (acc.iban?.iban_number) {
    // IBAN: show last 4 characters
    const iban = acc.iban.iban_number;
    maskedNumber = `IBAN ****${iban.slice(-4)}`;
  } else if (acc.us?.account_number) {
    // US account: show last 4 digits
    const accNum = acc.us.account_number;
    maskedNumber = `****${accNum.slice(-4)}`;
  } else if (acc.international_wire?.account_number) {
    // International wire: show last 4 digits
    const accNum = acc.international_wire.account_number;
    maskedNumber = `****${accNum.slice(-4)}`;
  }

  return `${accountName}${accountName && maskedNumber ? ' - ' : ''}${maskedNumber}`;
};